import { createFileRoute, useRouter, useNavigate, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Sparkles, Loader2, Undo2, Redo2, Wand2, Plus,
  Image as ImageIcon, Download, RotateCcw, Eraser, Layers,
  AlertTriangle, RefreshCw, UserPlus, Info, ClipboardList, MousePointer2, Maximize2,
} from "lucide-react";
import { Share2, FileText, Check } from "lucide-react";
import insp1 from "@/assets/inspiration/insp1.jpg.asset.json";
import insp2 from "@/assets/inspiration/insp2.jpg.asset.json";
import insp3 from "@/assets/inspiration/insp3.jpg.asset.json";
import insp4 from "@/assets/inspiration/insp4.jpg.asset.json";
import insp5 from "@/assets/inspiration/insp5.jpg.asset.json";
import insp6 from "@/assets/inspiration/insp6.jpg.asset.json";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { detectSurfaceMasks, refinePreview } from "@/lib/studio.functions";
import { SURFACE_LABELS, type SurfaceAssignment, type SurfaceKind, getAnonToken, getStudioClient } from "@/lib/studio";
import type { Segment } from "@/lib/studio/segments";
import { ColorPicker, type SelectedColor } from "@/components/colors/ColorPicker";
import { PaintCanvas, type CanvasAssignments, type CanvasFinishes, type SelectMode } from "@/components/studio/PaintCanvas";
import { FINISH_LABELS, LIGHTING_LABELS, type PaintFinish, type LightingMode } from "@/lib/studio/preview-engine";
import { validateUpload } from "@/lib/studio/upload-validation";
import { Pencil, Eye, EyeOff } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { BeforeAfterSlider } from "@/components/studio/BeforeAfterSlider";
import { ColorExplorer } from "@/components/studio/ColorExplorer";
import { StartScreen } from "@/components/studio/StartScreen";
import { ColorPickerDialog } from "@/components/studio/ColorPickerDialog";
import { duplicateSampleToProject, isBuiltinSample, saveAutosaveState, type PaintSample, type SavedStudioProject } from "@/lib/studio/samples";
import { track } from "@/lib/analytics/track";
import { createShare, shareUrl, type SharePaletteItem } from "@/lib/studio/shares";
import { generateSummaryPdf, downloadBlob } from "@/lib/studio/pdf";

export const Route = createFileRoute("/studio")({
  head: () => ({
    meta: [
      { title: "AI Paint Studio — Try Any Color Instantly" },
      { name: "description", content: "Upload a photo, click any surface, and recolor in under 100ms. AI handles detection while color changes stay instant and photorealistic." },
      { property: "og:title", content: "AI Paint Studio — Instant Color Visualizer" },
      { property: "og:description", content: "Real-time paint preview that preserves shadows, lighting, and texture. No AI wait per color." },
      { property: "og:url", content: "https://michaelspaintingwi.com/studio" },
    ],
    links: [{ rel: "canonical", href: "https://michaelspaintingwi.com/studio" }],
  }),
  component: StudioRoute,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-32 text-center bg-[#030303] min-h-screen">
        <p className="text-zinc-300 mb-4">The Studio hit an error.</p>
        <p className="text-zinc-500 text-sm mb-6">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2.5 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

type ColorChoice = { hex: string; name: string; number: string; brand: string; id: string; finish?: PaintFinish };
type State = Record<string, ColorChoice>; // segmentId -> color

function StudioRoute() {
  const navigate = useNavigate();
  const detect = useServerFn(detectSurfaceMasks);
  const refine = useServerFn(refinePreview);

  const [projectId, setProjectId] = useState<string | null>(null);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [history, setHistory] = useState<State[]>([{}]);
  const [hIdx, setHIdx] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isRendering, setIsRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null); // last AI render
  const [isSignedIn, setIsSignedIn] = useState<boolean | null>(null);
  const [activeTab, setActiveTab] = useState<"paint" | "explore" | "inspiration">("paint");
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  // Segment id currently hovered in the sidebar surfaces list, used to
  // highlight the corresponding region on the canvas.
  const [listHoverId, setListHoverId] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [lighting, setLighting] = useState<LightingMode>("natural");
  const [hiddenSegments, setHiddenSegments] = useState<Set<string>>(new Set());
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const autosaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [autosaveTick, setAutosaveTick] = useState<null | "saving" | "saved">(null);
  const hydratedRef = useRef(false);
  const [pickerExpanded, setPickerExpanded] = useState(false);

  const current: State = history[hIdx] ?? {};
  const assignmentsForCanvas: CanvasAssignments = useMemo(() => {
    const m: CanvasAssignments = {};
    for (const [id, c] of Object.entries(current)) m[id] = c.hex;
    return m;
  }, [current]);
  const finishesForCanvas: CanvasFinishes = useMemo(() => {
    const m: CanvasFinishes = {};
    for (const [id, c] of Object.entries(current)) if (c.finish) m[id] = c.finish;
    return m;
  }, [current]);
  // Filter out hidden segments so PaintCanvas won't rasterize/render them.
  const visibleSegments = useMemo(
    () => segments.filter((s) => !hiddenSegments.has(s.id)),
    [segments, hiddenSegments],
  );

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data }) => { if (!cancelled) setIsSignedIn(!!data.user); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setIsSignedIn(!!s?.user));
    return () => { cancelled = true; sub.subscription.unsubscribe(); };
  }, []);

  // Funnel entry: sessions that land on Studio start the Studio → Estimate flow
  useEffect(() => {
    track.funnel("studio_to_estimate", "studio_open", 0);
  }, []);

  // ----- Session persistence: keep in-progress work across navigation -----
  // Restore from localStorage on mount so the user's uploaded photo + color
  // scheme comes back if they navigate to another page and return.
  useEffect(() => {
    try {
      const raw = window.localStorage.getItem("mp_studio_session");
      if (raw) {
        const s = JSON.parse(raw) as {
          projectId: string | null;
          sourceUrl: string | null;
          segments: Segment[];
          history: State[];
          hIdx: number;
          hiddenSegments: string[];
          lighting: LightingMode;
          photoUrl: string | null;
        };
        if (s.sourceUrl) {
          setProjectId(s.projectId);
          setSourceUrl(s.sourceUrl);
          setSegments(s.segments ?? []);
          setHistory(s.history?.length ? s.history : [{}]);
          setHIdx(s.hIdx ?? 0);
          setHiddenSegments(new Set(s.hiddenSegments ?? []));
          setLighting(s.lighting ?? "natural");
          setPhotoUrl(s.photoUrl ?? null);
        }
      }
    } catch { /* ignore corrupt session */ }
    hydratedRef.current = true;
  }, []);

  // Persist the working session whenever it changes (after hydration).
  useEffect(() => {
    if (!hydratedRef.current) return;
    try {
      if (!sourceUrl) {
        window.localStorage.removeItem("mp_studio_session");
        return;
      }
      window.localStorage.setItem("mp_studio_session", JSON.stringify({
        projectId,
        sourceUrl,
        segments,
        history,
        hIdx,
        hiddenSegments: Array.from(hiddenSegments),
        lighting,
        photoUrl,
      }));
    } catch { /* quota / serialization errors are non-fatal */ }
  }, [projectId, sourceUrl, segments, history, hIdx, hiddenSegments, lighting, photoUrl]);

  // ----- History helpers -----
  const pushState = useCallback((updater: (s: State) => State) => {
    setHistory((prev) => {
      const base = prev[hIdx] ?? {};
      const next = updater(base);
      const truncated = prev.slice(0, hIdx + 1);
      return [...truncated, next];
    });
    setHIdx((i) => i + 1);
  }, [hIdx]);

  const canUndo = hIdx > 0;
  const canRedo = hIdx < history.length - 1;

  // ----- Upload + detect -----
  const handleUpload = useCallback(async (file: File) => {
    setError(null);
    const check = await validateUpload(file);
    if (!check.ok) { setError(check.reason); return; }
    setIsUploading(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id ?? null;
      const anonTok = getAnonToken();
      const uploadFile = check.file;
      const ext = (uploadFile.name.split(".").pop() ?? "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
      // Guests upload under `guest/<anon_token>/...`; signed-in users under their uid folder.
      const folder = uid ? uid : `guest/${anonTok}`;
      const path = `${folder}/${crypto.randomUUID()}/source.${ext}`;
      const { error: upErr } = await supabase.storage.from("studio").upload(path, uploadFile, { upsert: false, contentType: uploadFile.type || "image/jpeg" });
      if (upErr) throw upErr;
      const { data: { publicUrl } } = supabase.storage.from("studio").getPublicUrl(path);

      const studioClient = await getStudioClient();
      const { data: proj, error: projErr } = await studioClient
        .from("studio_projects")
        .insert({
          name: file.name.replace(/\.[^.]+$/, ""),
          source_image_url: publicUrl,
          source_image_width: check.width,
          source_image_height: check.height,
          user_id: uid,
          anon_token: uid ? null : anonTok,
        })
        .select("id")
        .single();
      if (projErr) throw projErr;


      setProjectId(proj.id);
      setSourceUrl(publicUrl);
      setSegments([]);
      setSelected([]);
      setHiddenSegments(new Set());
      setHistory([{}]);
      setHIdx(0);
      setPhotoUrl(null);
      setIsUploading(false);
      setIsDetecting(true);

      track.funnel("studio_to_estimate", "photo_uploaded", 1, { projectId: proj.id, width: check.width, height: check.height });

      const { segments: segs } = await detect({ data: { imageUrl: publicUrl, projectId: proj.id, anonToken: getAnonToken() } });
      // Ensure unique ids
      const ided = segs.map((s, i) => ({ ...s, id: s.id || `seg-${i}` }));
      setSegments(ided);
      track.funnel("studio_to_estimate", "surfaces_detected", 2, { projectId: proj.id, count: ided.length });
    } catch (e) {
      setError((e as Error).message);
      track.event("studio_upload_failed", { message: (e as Error).message });
    } finally {
      setIsUploading(false);
      setIsDetecting(false);
    }
  }, [detect]);

  // Re-run detection on the current photo (explicit user action).
  const handleDetectAgain = useCallback(async () => {
    if (!sourceUrl) return;
    setError(null);
    setIsDetecting(true);
    try {
      const { segments: segs } = await detect({ data: { imageUrl: sourceUrl, projectId: projectId ?? undefined, anonToken: getAnonToken() } });
      const ided = segs.map((s, i) => ({ ...s, id: s.id || `seg-${i}` }));
      setSegments(ided);
      setSelected([]);
      setHistory([{}]);
      setHIdx(0);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsDetecting(false);
    }
  }, [detect, projectId, sourceUrl]);

  // ----- Load a sample project (pre-masked demo) -----
  const handleUseSample = useCallback(async (sample: PaintSample) => {
    setError(null);
    setIsUploading(true);
    try {
      // Built-in fallback samples: no DB row, no auth required. Point the
      // studio at the sample image and run the same AI detection pipeline
      // as a fresh upload so the user can immediately click surfaces.
      if (isBuiltinSample(sample)) {
        const absUrl = new URL(sample.source_image_url, window.location.origin).toString();
        setProjectId(null);
        setSourceUrl(absUrl);
        setSegments([]);
        setSelected([]);
        setHiddenSegments(new Set());
        setHistory([{}]);
        setHIdx(0);
        setPhotoUrl(null);
        setIsUploading(false);
        setIsDetecting(true);
        try {
          const { segments: segs } = await detect({ data: { imageUrl: absUrl, anonToken: getAnonToken() } });
          setSegments(segs.map((s, i) => ({ ...s, id: s.id || `seg-${i}` })));
          track.event("studio_builtin_sample_opened", { sampleId: sample.id });
        } catch (e) {
          setError((e as Error).message);
        } finally {
          setIsDetecting(false);
        }
        return;
      }
      const { projectId: pid } = await duplicateSampleToProject(sample, getAnonToken());
      setProjectId(pid);
      setSourceUrl(sample.source_image_url);
      const segs = (sample.segments ?? []).map((s, i) => ({ ...s, id: s.id || `seg-${i}` }));
      setSegments(segs);
      // Seed initial color assignments from the sample's defaults
      const initial: State = {};
      for (const [sid, c] of Object.entries(sample.default_assignments ?? {})) initial[sid] = c;
      setHistory([initial]);
      setHIdx(0);
      setSelected([]);
      setPhotoUrl(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsUploading(false);
    }
  }, [detect]);

  // ----- Reopen a previously saved project -----
  const handleOpenSaved = useCallback(async (p: SavedStudioProject) => {
    setError(null);
    setProjectId(p.id);
    setSourceUrl(p.source_image_url);
    setSegments([]);
    setSelected([]);
    setHistory([{}]);
    setHIdx(0);
    setPhotoUrl(p.latest_render_url);
    // Restore autosaved scheme if present
    const a = p.autosave_state as
      | { assignments?: State; hidden?: string[]; lighting?: LightingMode }
      | null
      | undefined;
    if (a?.assignments) setHistory([a.assignments]);
    if (a?.hidden) setHiddenSegments(new Set(a.hidden));
    if (a?.lighting) setLighting(a.lighting);
    track.event("studio_project_reopened", { projectId: p.id, hasAutosave: !!a?.assignments });
    // Re-run detection so the user can immediately edit surfaces
    setIsDetecting(true);
    try {
      const { segments: segs } = await detect({ data: { imageUrl: p.source_image_url, projectId: p.id, anonToken: getAnonToken() } });
      setSegments(segs.map((s, i) => ({ ...s, id: s.id || `seg-${i}` })));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsDetecting(false);
    }
  }, [detect]);

  // ----- Autosave: debounced write of current scheme to studio_projects.autosave_state -----
  useEffect(() => {
    if (!projectId) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(async () => {
      setAutosaveTick("saving");
      try {
        await saveAutosaveState(projectId, {
          assignments: current as unknown as Record<string, { hex: string; name: string; number: string; brand: string; id: string; finish?: string }>,
          hidden: Array.from(hiddenSegments),
          lighting,
        });
        setAutosaveTick("saved");
        setTimeout(() => setAutosaveTick(null), 1500);
      } catch {
        setAutosaveTick(null);
      }
    }, 1200);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
  }, [projectId, current, hiddenSegments, lighting]);

  // ----- Selection -----
  const handleSelect = useCallback((id: string, mode: SelectMode) => {
    setSelected((prev) => {
      if (mode === "add") return prev.includes(id) ? prev : [...prev, id];
      if (mode === "remove") return prev.filter((x) => x !== id);
      if (mode === "connected") {
        // Connected = all segments of same kind
        const target = segments.find((s) => s.id === id);
        if (!target) return prev;
        const sameKind = segments.filter((s) => s.kind === target.kind).map((s) => s.id);
        return Array.from(new Set([...prev, ...sameKind]));
      }
      return [id];
    });
  }, [segments]);

  // ----- Color picking (instant, local) -----
  const onPickColor = useCallback((color: SelectedColor) => {
    if (selected.length === 0) {
      setError("Click a surface in the photo first, then pick a color.");
      return;
    }
    setError(null);
    const choice: ColorChoice = {
      hex: color.hex,
      name: color.color_name,
      number: color.color_number,
      brand: color.brand,
      id: color.id,
      finish: current[selected[0]]?.finish ?? "matte",
    };
    pushState((s) => {
      const next = { ...s };
      for (const sid of selected) next[sid] = choice;
      return next;
    });
    track.funnel("studio_to_estimate", "color_applied", 3, { colorId: color.id, surfaces: selected.length });
  }, [selected, pushState, current]);

  // Change finish for currently selected surfaces (only those that already have a color).
  const setFinishForSelected = useCallback((finish: PaintFinish) => {
    if (selected.length === 0) return;
    pushState((s) => {
      const next = { ...s };
      for (const sid of selected) {
        const c = next[sid];
        if (c) next[sid] = { ...c, finish };
      }
      return next;
    });
  }, [selected, pushState]);

  const clearSelectedColors = useCallback(() => {
    if (selected.length === 0) return;
    pushState((s) => {
      const next = { ...s };
      for (const sid of selected) delete next[sid];
      return next;
    });
  }, [selected, pushState]);

  const clearAll = useCallback(() => {
    pushState(() => ({}));
    setSelected([]);
  }, [pushState]);

  // ----- Keyboard shortcuts -----
  // Speeds up review triage: [ / ] cycles selection through segments in the
  // sidebar order, H toggles visibility of the selected surfaces, Backspace
  // clears their paint color, Escape drops the selection. Shortcuts are
  // ignored while typing in an input/textarea/contenteditable so renaming
  // and other text entry keep working.
  useEffect(() => {
    if (segments.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable) return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "[" || e.key === "]") {
        e.preventDefault();
        const dir = e.key === "]" ? 1 : -1;
        const idx = selected.length === 1 ? segments.findIndex((s) => s.id === selected[0]) : -1;
        const nextIdx = ((idx === -1 ? (dir === 1 ? -1 : 0) : idx) + dir + segments.length) % segments.length;
        const next = segments[nextIdx];
        if (next) { setSelected([next.id]); setListHoverId(next.id); }
      } else if (e.key === "Escape") {
        if (selected.length) { e.preventDefault(); setSelected([]); }
      } else if (e.key === "h" || e.key === "H") {
        if (selected.length === 0) return;
        e.preventDefault();
        setHiddenSegments((prev) => {
          const n = new Set(prev);
          const shouldHide = selected.some((sid) => !n.has(sid));
          for (const sid of selected) { if (shouldHide) n.add(sid); else n.delete(sid); }
          return n;
        });
      } else if (e.key === "Backspace" || e.key === "Delete") {
        if (selected.length === 0) return;
        if (!selected.some((sid) => current[sid])) return;
        e.preventDefault();
        pushState((s) => { const n = { ...s }; for (const sid of selected) delete n[sid]; return n; });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [segments, selected, current, pushState]);

  // ----- Photorealistic AI render -----
  const handleGenerate = useCallback(async () => {
    if (!projectId || !sourceUrl) return;
    const assignments: SurfaceAssignment[] = Object.entries(current).map(([sid, c]) => {
      const seg = segments.find((s) => s.id === sid);
      return {
        kind: (seg?.kind ?? "walls") as SurfaceKind,
        colorId: c.id, colorName: c.name, colorNumber: c.number, brand: c.brand, hex: c.hex,
      };
    });
    if (assignments.length === 0) {
      setRenderError("Pick at least one color first.");
      return;
    }
    setIsRendering(true);
    setRenderError(null);
    track.event("studio_render_started", { projectId, surfaces: assignments.length });
    try {
      // Snapshot the in-browser preview. That image already has the right
      // color on the right surface — Gemini's job is photorealistic polish only.
      const canvas = document.querySelector("canvas") as HTMLCanvasElement | null;
      if (!canvas) throw new Error("Preview canvas not ready.");
      const previewDataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const palette = Object.entries(current).map(([sid, c]) => {
        const seg = segments.find((s) => s.id === sid);
        return {
          label: seg?.label || SURFACE_LABELS[(seg?.kind ?? "walls") as SurfaceKind] || "surface",
          colorName: c.name,
          colorNumber: c.number,
          hex: c.hex,
        };
      });
      const { dataUrl } = await refine({
        data: { previewImageUrl: previewDataUrl, sourceImageUrl: sourceUrl, palette, projectId: projectId ?? undefined, anonToken: getAnonToken() },
      });
      const blob = await (await fetch(dataUrl)).blob();
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id ?? null;
      const anonTok = getAnonToken();
      let publicUrl = dataUrl;

      if (projectId) {
        const folder = uid ? uid : `guest/${anonTok}`;
        const path = `${folder}/${projectId}/${crypto.randomUUID()}.png`;
        const { error: upErr } = await supabase.storage.from("studio").upload(path, blob, { contentType: "image/png" });
        if (!upErr) {
          const { data } = supabase.storage.from("studio").getPublicUrl(path);
          publicUrl = data.publicUrl;
        }
      }
      // Persist the version only when the current session owns this project
      // (either as signed-in owner or as the guest that created it).
      if (projectId) {
        let owned = false;
        const studioClient = await getStudioClient();
        if (uid) {
          const { data } = await studioClient
            .from("studio_projects").select("id")
            .eq("id", projectId).eq("user_id", uid).maybeSingle();
          owned = !!data;
        } else {
          const { data } = await studioClient
            .from("studio_projects").select("id")
            .eq("id", projectId).eq("anon_token", anonTok).maybeSingle();
          owned = !!data;
        }
        if (owned) {
          const { error: verErr } = await studioClient.from("studio_versions").insert({
            project_id: projectId,
            label: `Photorealistic ${new Date().toLocaleTimeString()}`,
            rendered_image_url: publicUrl,
            surface_assignments: assignments,
          });
          if (verErr) console.warn("Failed to save version:", verErr.message);
        }
      }
      setPhotoUrl(publicUrl);
      track.funnel("studio_to_estimate", "render_completed", 4, { projectId });
    } catch (e) {
      setRenderError((e as Error).message || "AI render failed.");
      track.event("studio_render_failed", { message: (e as Error).message });
    } finally {
      setIsRendering(false);
    }
  }, [current, projectId, refine, segments, sourceUrl]);

  // ----- Estimate handoff -----
  const goToEstimate = useCallback(() => {
    track.funnel("studio_to_estimate", "estimate_started", 5, { projectId, hasRender: !!photoUrl });
    const palette = Object.entries(current).map(([sid, c]) => {
      const seg = segments.find((s) => s.id === sid);
      return { id: c.id, name: c.name, number: c.number, hex: c.hex, surface: (seg?.kind ?? "walls") as SurfaceKind };
    });
    try {
      window.sessionStorage.setItem("mp_estimate_attachment", JSON.stringify({
        studioProjectId: projectId,
        studioVersionId: null,
        designImageUrl: photoUrl ?? sourceUrl,
        selectedColors: palette,
        favoriteColors: [],
      }));
    } catch { /* noop */ }
    navigate({ to: "/estimate", search: { project: projectId ?? undefined, image: (photoUrl ?? sourceUrl) ?? undefined } });
  }, [current, navigate, photoUrl, projectId, segments, sourceUrl]);

  // ----- Build a portable palette snapshot for share + PDF -----
  const buildPalette = useCallback((): SharePaletteItem[] => {
    return Object.entries(current).map(([sid, c]) => {
      const seg = segments.find((s) => s.id === sid);
      return {
        surface: seg?.label || SURFACE_LABELS[(seg?.kind ?? "walls") as SurfaceKind] || "surface",
        hex: c.hex,
        name: c.name,
        number: c.number,
        brand: c.brand,
      };
    });
  }, [current, segments]);

  // ----- Share link (snapshot of current scheme) -----
  const handleShare = useCallback(async () => {
    if (!sourceUrl) return;
    setShareBusy(true);
    setError(null);
    try {
      const share = await createShare({
        projectId,
        name: null,
        sourceImageUrl: sourceUrl,
        renderedImageUrl: photoUrl,
        palette: buildPalette(),
      });
      const url = shareUrl(share.token);
      try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2500);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setShareBusy(false);
    }
  }, [buildPalette, photoUrl, projectId, sourceUrl]);

  // ----- PDF summary -----
  const handlePdf = useCallback(async () => {
    if (!sourceUrl) return;
    setPdfBusy(true);
    try {
      const blob = await generateSummaryPdf({
        projectName: "Color scheme",
        sourceImageUrl: sourceUrl,
        renderedImageUrl: photoUrl,
        palette: buildPalette(),
      });
      downloadBlob(blob, "color-scheme.pdf");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPdfBusy(false);
    }
  }, [buildPalette, photoUrl, sourceUrl]);

  // ----- Keyboard shortcuts -----
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === "z" && !e.shiftKey) { if (canUndo) { setHIdx((i) => i - 1); e.preventDefault(); } }
      else if ((meta && e.key.toLowerCase() === "y") || (meta && e.shiftKey && e.key.toLowerCase() === "z")) { if (canRedo) { setHIdx((i) => i + 1); e.preventDefault(); } }
      else if (e.key === "Escape") setSelected([]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canUndo, canRedo]);

  const selectedSegments = selected
    .map((id) => segments.find((s) => s.id === id))
    .filter(Boolean) as Segment[];
  const assignedCount = Object.keys(current).length;

  return (
    <section className="relative bg-[#030303] min-h-screen text-white">
      <div className="absolute top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-amber-500/15 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-yellow-500/10 blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 lg:pt-24 pb-32 lg:pb-24 relative">
        <header className="max-w-3xl mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[10px] sm:text-[11px] font-medium tracking-widest uppercase text-amber-200 mb-4 sm:mb-5">
            <Sparkles className="size-3" /> AI Paint Studio
          </div>
          <h1 className="font-serif text-[2rem] leading-[1.05] sm:text-4xl md:text-5xl tracking-tight mb-3 sm:mb-4">
            Click any surface. <span className="italic text-amber-200/90">Pick any color.</span> Instantly.
          </h1>
          <p className="text-zinc-400 text-base sm:text-lg leading-relaxed">
            Our AI neural engine identifies your walls, trim, and ceilings in seconds. Experience thousands of Sherwin-Williams hues with photorealistic, zero-latency rendering.
          </p>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-8 border-b border-white/10">
          {[
            { id: "paint" as const, label: "Paint an Image" },
            { id: "explore" as const, label: "Explore Color" },
            { id: "inspiration" as const, label: "Inspiration" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`px-3 sm:px-4 py-2.5 text-[11px] sm:text-xs font-medium tracking-wider uppercase transition border-b-2 ${
                activeTab === t.id
                  ? "text-amber-200 border-amber-300"
                  : "text-zinc-500 border-transparent hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {activeTab === "paint" && (!sourceUrl ? (
          <div className="flex flex-col items-center">
            <StartScreen
              isSignedIn={isSignedIn}
              onChooseUpload={() => { /* upload path is handled by the file input inside UploadCard below */ }}
              onChooseSample={handleUseSample}
              onOpenSaved={handleOpenSaved}
            />
            <div className="mt-10 w-full flex flex-col items-center">
              <p className="text-center text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Or upload your own photo</p>
              <UploadCard onPick={handleUpload} isUploading={isUploading} error={error} />
            </div>
            <StudioEmptyFooter />
            <StudioHowItWorks />
            <StudioDemoGallery />
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_500px] xl:grid-cols-[1fr_560px] gap-4 lg:gap-6 lg:items-start">
            {/* Canvas column */}
            <div className="space-y-4">
              {/* Toolbar */}
              <div className="flex items-center justify-between flex-wrap gap-2 no-scrollbar overflow-x-auto -mx-1 px-1">
                <div className="flex items-center gap-1.5 shrink-0">
                  <ToolbarBtn disabled={!canUndo} onClick={() => setHIdx((i) => i - 1)} icon={<Undo2 className="size-3.5" />} label="Undo" />
                  <ToolbarBtn disabled={!canRedo} onClick={() => setHIdx((i) => i + 1)} icon={<Redo2 className="size-3.5" />} label="Redo" />
                  <ToolbarBtn disabled={selected.length === 0} onClick={clearSelectedColors} icon={<Eraser className="size-3.5" />} label="Clear color" />
                  <ToolbarBtn disabled={assignedCount === 0} onClick={clearAll} icon={<RotateCcw className="size-3.5" />} label="Reset all" />
                  <ToolbarBtn disabled={isDetecting || !sourceUrl} onClick={handleDetectAgain} icon={<Wand2 className="size-3.5" />} label="Detect again" />
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <ToolbarBtn
                    onClick={() => downloadCanvas("paint-preview.png")}
                    icon={<Download className="size-3.5" />} label="PNG"
                  />
                  {autosaveTick && (
                    <span className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                      {autosaveTick === "saving" ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3 text-emerald-400" />}
                      {autosaveTick === "saving" ? "Saving…" : "Saved"}
                    </span>
                  )}
                  <button
                    onClick={() => { setSourceUrl(null); setSegments([]); setSelected([]); setHistory([{}]); setHIdx(0); setPhotoUrl(null); }}
                    className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1 ml-2"
                  ><Plus className="size-3" /> New photo</button>
                </div>
              </div>

              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-zinc-900">
                <PaintCanvas
                  sourceUrl={sourceUrl}
                  segments={visibleSegments}
                  assignments={assignmentsForCanvas}
                  finishes={finishesForCanvas}
                  lighting={lighting}
                  selected={selected}
                  externalHoverId={listHoverId}
                  onSelect={handleSelect}
                  onClickEmpty={() => setSelected([])}
                />
                {isDetecting && (
                  <div className="absolute inset-0 grid place-items-center bg-black/60 backdrop-blur-sm">
                    <div className="text-center">
                      <Loader2 className="size-6 animate-spin text-amber-300 mx-auto mb-3" />
                      <p className="text-sm text-zinc-200">Finding paintable surfaces…</p>
                      <p className="text-[11px] text-zinc-500 mt-1">One-time AI step. Color changes after this will be instant.</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Interaction hints */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-zinc-500">
                <span className="inline-flex items-center gap-1.5"><MousePointer2 className="size-3" /> Click to select</span>
                <span>Shift-click to add · Ctrl/⌘-click to remove · Double-click for same surface type</span>
              </div>

              {/* Photorealistic render preview with before/after slider */}
              {photoUrl && sourceUrl && (
                <div className="rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-[10px] uppercase tracking-widest text-amber-200 inline-flex items-center gap-1.5">
                      <Wand2 className="size-3" /> Photorealistic version · drag to compare
                    </p>
                    <div className="flex items-center gap-3">
                      <a href={photoUrl} target="_blank" rel="noreferrer" className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1">Open</a>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const res = await fetch(photoUrl, { mode: "cors" });
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url;
                            a.download = `photorealistic-${Date.now()}.png`;
                            document.body.appendChild(a); a.click(); a.remove();
                            setTimeout(() => URL.revokeObjectURL(url), 1000);
                          } catch {
                            window.open(photoUrl, "_blank");
                          }
                        }}
                        className="text-[11px] inline-flex items-center gap-1.5 rounded-md bg-amber-300/90 hover:bg-amber-300 text-zinc-900 font-medium px-2.5 py-1"
                      >
                        <Download className="size-3" /> Download
                      </button>
                    </div>
                  </div>
                  <BeforeAfterSlider before={sourceUrl} after={photoUrl} />
                </div>
              )}
            </div>

            {/* Sidebar — sticky so surfaces + colors stay visible together */}
            <aside className="lg:sticky lg:top-4 lg:h-[calc(100vh-2rem)] flex flex-col gap-3 min-h-0">
              {/* Surfaces list — capped height, scrollable */}
              {segments.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-3 flex-none flex flex-col min-h-0 max-h-[40vh] lg:max-h-[38%]">
                  <div className="flex items-center justify-between px-1 mb-2 flex-none">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
                      <Layers className="size-3" /> Surfaces · {segments.length}
                    </p>
                    <div className="flex items-center gap-2">
                      {(() => {
                        // Count segments the AI flagged as needing review so
                        // the user can decide whether the detection is
                        // trustworthy before painting.
                        const reviewIds = segments
                          .filter((s) => s.reviewStatus && s.reviewStatus !== "high")
                          .map((s) => s.id);
                        if (reviewIds.length === 0) return null;
                        const currentIdx = selected.length === 1
                          ? reviewIds.indexOf(selected[0])
                          : -1;
                        const nextId = reviewIds[(currentIdx + 1) % reviewIds.length];
                        return (
                          <button
                            onClick={() => {
                              setSelected([nextId]);
                              setListHoverId(nextId);
                            }}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300/40 text-amber-100 hover:bg-amber-300/10"
                            title="Jump to the next surface the AI wasn't sure about."
                          >
                            Review {reviewIds.length}
                          </button>
                        );
                      })()}
                      {selected.length === 1 && (() => {
                        const one = segments.find((s) => s.id === selected[0]);
                        if (!one || one.polygons.length < 2) return null;
                        return (
                          <button
                            onClick={() => {
                              // Split a multi-polygon segment into one segment
                              // per polygon. Useful when the AI grouped two
                              // visually distinct walls under one label.
                              const parts: Segment[] = one.polygons.map((poly, i) => ({
                                id: `${one.id}-part-${i}-${crypto.randomUUID().slice(0, 6)}`,
                                kind: one.kind,
                                label: `${one.label || SURFACE_LABELS[one.kind] || one.kind} ${i + 1}`,
                                polygons: [poly],
                                confidence: one.confidence,
                                reviewStatus: one.reviewStatus,
                                source: "manual",
                              }));
                              setSegments((prev) => [
                                ...prev.filter((s) => s.id !== one.id),
                                ...parts,
                              ]);
                              setSelected([]);
                              // Preserve any color assignment across all parts.
                              const existing = current[one.id];
                              if (existing) {
                                pushState((s) => {
                                  const next = { ...s };
                                  delete next[one.id];
                                  for (const p of parts) next[p.id] = existing;
                                  return next;
                                });
                              }
                            }}
                            className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300/40 text-amber-100 hover:bg-amber-300/10"
                            title="Break this surface into its individual regions."
                          >
                            Split
                          </button>
                        );
                      })()}
                      {selected.length >= 2 && (
                        <button
                          onClick={() => {
                            // Merge selected segments into a single segment.
                            // The first-selected segment defines kind/label; all
                            // polygons are concatenated. Confidence collapses to
                            // the max of the group, source becomes "manual".
                            const ids = selected;
                            const chosen = ids
                              .map((id) => segments.find((s) => s.id === id))
                              .filter((s): s is Segment => Boolean(s));
                            if (chosen.length < 2) return;
                            const base = chosen[0];
                            const mergedId = `merged-${crypto.randomUUID().slice(0, 8)}`;
                            const merged: Segment = {
                              id: mergedId,
                              kind: base.kind,
                              label: base.label || SURFACE_LABELS[base.kind] || base.kind,
                              polygons: chosen.flatMap((s) => s.polygons),
                              confidence: Math.max(...chosen.map((s) => s.confidence ?? 0.7)),
                              reviewStatus: "high",
                              source: "manual",
                            };
                            setSegments((prev) => [
                              ...prev.filter((s) => !ids.includes(s.id)),
                              merged,
                            ]);
                            setSelected([mergedId]);
                          }}
                          className="text-[10px] px-2 py-0.5 rounded-full border border-amber-300/40 text-amber-100 hover:bg-amber-300/10"
                          title="Combine the selected surfaces into one so they share a paint color."
                        >
                          Merge {selected.length}
                        </button>
                      )}
                      {selected.length > 0 && (
                        <button onClick={() => setSelected([])} className="text-[10px] text-zinc-500 hover:text-white">Clear</button>
                      )}
                    </div>
                  </div>
                  <ul className="space-y-1 overflow-y-auto pr-1 -mr-1 flex-1 min-h-0">
                    {segments.map((seg) => {
                      const isSel = selected.length === 1 && selected[0] === seg.id;
                      const isInSel = selected.includes(seg.id);
                      const c = current[seg.id];
                      const hidden = hiddenSegments.has(seg.id);
                      const isRenaming = renamingId === seg.id;
                      return (
                        <li key={seg.id}>
                          <div
                            className={`group flex items-center gap-2 px-2.5 py-2 rounded-xl border transition ${hidden ? "opacity-50" : ""} ${isSel ? "border-amber-300/60 bg-amber-300/10" : isInSel ? "border-amber-300/30 bg-amber-300/5" : "border-white/5 bg-white/[0.02] hover:border-white/15 hover:bg-white/[0.04]"} ${isRenaming ? "" : "cursor-pointer"}`}
                            onClick={(e) => { if (isRenaming) return; handleSelect(seg.id, e.shiftKey ? "add" : (e.ctrlKey || e.metaKey) ? "remove" : "replace"); }}
                            onMouseEnter={() => setListHoverId(seg.id)}
                            onMouseLeave={() => setListHoverId((v) => (v === seg.id ? null : v))}
                          >
                            <span
                              className="size-7 rounded-md border border-white/15 flex-none grid place-items-center"
                              style={{ backgroundColor: c?.hex ?? "transparent", backgroundImage: c ? undefined : "repeating-linear-gradient(45deg,rgba(255,255,255,0.06) 0 4px,transparent 4px 8px)" }}
                            >
                              {!c && <Plus className="size-3 text-zinc-500" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              {isRenaming ? (
                                <input
                                  autoFocus
                                  value={renameValue}
                                  onChange={(e) => setRenameValue(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const v = renameValue.trim().slice(0, 40);
                                      if (v) setSegments((prev) => prev.map((s) => s.id === seg.id ? { ...s, label: v } : s));
                                      setRenamingId(null);
                                    } else if (e.key === "Escape") { setRenamingId(null); }
                                  }}
                                  onBlur={() => {
                                    const v = renameValue.trim().slice(0, 40);
                                    if (v) setSegments((prev) => prev.map((s) => s.id === seg.id ? { ...s, label: v } : s));
                                    setRenamingId(null);
                                  }}
                                  maxLength={40}
                                  className="w-full bg-black/40 border border-white/15 rounded px-1.5 py-0.5 text-[12px] text-white"
                                />
                              ) : (
                                <p className="text-[12px] text-zinc-100 truncate capitalize flex items-center gap-1.5">
                                  <span className="truncate">{seg.label || SURFACE_LABELS[seg.kind] || seg.kind}</span>
                                  {seg.reviewStatus && seg.reviewStatus !== "high" && (
                                    <span
                                      title={seg.reviewStatus === "low"
                                        ? "Low confidence — check this surface before painting."
                                        : "Review recommended — the AI wasn't fully sure of this boundary."}
                                      className={`flex-none normal-case text-[9px] px-1.5 py-0.5 rounded-full border ${
                                        seg.reviewStatus === "low"
                                          ? "border-rose-400/40 text-rose-200 bg-rose-500/10"
                                          : "border-amber-300/40 text-amber-100 bg-amber-400/10"
                                      }`}
                                    >
                                      {seg.reviewStatus === "low" ? "Low" : "Review"}
                                    </span>
                                  )}
                                </p>
                              )}
                              <p className="text-[10px] text-zinc-500 truncate">
                                {c ? `${c.name} · ${c.number}` : "No color yet"}
                              </p>
                            </div>
                            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                              <button
                                onClick={(e) => { e.stopPropagation(); setRenamingId(seg.id); setRenameValue(seg.label || SURFACE_LABELS[seg.kind] || seg.kind); }}
                                className="text-zinc-500 hover:text-white p-1"
                                title="Rename surface"
                              ><Pencil className="size-3.5" /></button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setHiddenSegments((prev) => {
                                    const n = new Set(prev);
                                    if (n.has(seg.id)) n.delete(seg.id); else n.add(seg.id);
                                    return n;
                                  });
                                }}
                                className="text-zinc-500 hover:text-white p-1"
                                title={hidden ? "Show surface" : "Hide surface"}
                              >{hidden ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}</button>
                              {c && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    pushState((s) => { const n = { ...s }; delete n[seg.id]; return n; });
                                  }}
                                  className="text-zinc-500 hover:text-white p-1"
                                  title="Clear color"
                                ><Eraser className="size-3.5" /></button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* Color picker — fills remaining sidebar height, always visible */}
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex-1 min-h-0 flex flex-col max-h-[70vh] lg:max-h-none">
                {/* Finish (per selection) + Lighting (global) */}
                <div className="mb-3 flex-none space-y-2">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Finish</p>
                    <div className="flex flex-wrap gap-1">
                      {(Object.keys(FINISH_LABELS) as PaintFinish[]).map((f) => {
                        const activeFinish = selected.length > 0 ? (current[selected[0]]?.finish ?? "matte") : null;
                        const enabled = selected.length > 0 && selected.some((sid) => current[sid]);
                        const isActive = enabled && activeFinish === f;
                        return (
                          <button
                            key={f}
                            disabled={!enabled}
                            onClick={() => setFinishForSelected(f)}
                            className={`px-2 py-1 rounded-full text-[10px] border transition ${
                              isActive
                                ? "border-amber-300/60 bg-amber-300/15 text-amber-100"
                                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/25 disabled:opacity-30 disabled:cursor-not-allowed"
                            }`}
                            title={enabled ? `Apply ${FINISH_LABELS[f]} finish` : "Assign a color first"}
                          >
                            {FINISH_LABELS[f]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Lighting</p>
                    <div className="flex flex-wrap gap-1">
                      {(Object.keys(LIGHTING_LABELS) as LightingMode[]).map((m) => {
                        const isActive = lighting === m;
                        return (
                          <button
                            key={m}
                            onClick={() => setLighting(m)}
                            className={`px-2 py-1 rounded-full text-[10px] border transition ${
                              isActive
                                ? "border-amber-300/60 bg-amber-300/15 text-amber-100"
                                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/25"
                            }`}
                          >
                            {LIGHTING_LABELS[m]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-2 flex-none">
                  <p className="text-[10px] uppercase tracking-widest text-zinc-500">Sherwin-Williams colors</p>
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-[10px] text-amber-200/80 truncate">
                      {selected.length === 0
                        ? "Pick a surface ↑"
                        : selected.length === 1
                          ? `Applies to: ${selectedSegments[0]?.label || SURFACE_LABELS[selectedSegments[0]?.kind ?? "walls"]}`
                          : `Applies to ${selected.length} surfaces`}
                    </p>
                    <button
                      onClick={() => setPickerExpanded(true)}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20 px-2 py-1 text-[10px] uppercase tracking-wider transition"
                      title="Open full color library"
                    >
                      <Maximize2 className="size-3" /> Expand
                    </button>
                  </div>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2">
                  <ColorPicker onSelect={onPickColor} />
                </div>
                {error && <p className="text-[11px] text-red-400 mt-2 flex-none">{error}</p>}
              </div>

              {/* Compact action bar — always visible at bottom of sidebar */}
              <div className="hidden lg:flex flex-none rounded-2xl border border-amber-300/20 bg-gradient-to-br from-amber-300/5 to-yellow-500/0 p-3 items-center gap-2">
                <button
                  onClick={handleGenerate}
                  disabled={isRendering || assignedCount === 0}
                  className="flex-1 bg-amber-300 text-zinc-950 font-semibold py-2.5 rounded-full hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed transition inline-flex items-center justify-center gap-1.5 text-xs"
                  title={assignedCount === 0 ? "Pick a color first" : `Render ${assignedCount} surface${assignedCount === 1 ? "" : "s"}`}
                >
                  {isRendering ? (<><Loader2 className="size-3.5 animate-spin" /> Rendering…</>) : (<><Wand2 className="size-3.5" /> Photorealistic</>)}
                </button>
                <button
                  onClick={goToEstimate}
                  className="flex-1 bg-white text-zinc-950 font-semibold py-2.5 rounded-full text-xs hover:bg-amber-300 transition"
                >Get Estimate</button>
              </div>
              {/* Share + PDF secondary row */}
              <div className="hidden lg:flex flex-none items-center gap-2">
                <button
                  onClick={handleShare}
                  disabled={shareBusy || assignedCount === 0}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/5 text-[11px] text-zinc-200 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title={assignedCount === 0 ? "Pick a color first" : "Copy a shareable link"}
                >
                  {shareBusy ? <Loader2 className="size-3.5 animate-spin" /> : shareCopied ? <Check className="size-3.5 text-emerald-400" /> : <Share2 className="size-3.5" />}
                  {shareCopied ? "Link copied" : "Share link"}
                </button>
                <button
                  onClick={handlePdf}
                  disabled={pdfBusy || assignedCount === 0}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-white/10 bg-white/5 text-[11px] text-zinc-200 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  title={assignedCount === 0 ? "Pick a color first" : "Download PDF summary"}
                >
                  {pdfBusy ? <Loader2 className="size-3.5 animate-spin" /> : <FileText className="size-3.5" />}
                  PDF summary
                </button>
              </div>
              {renderError && (
                <div className="flex-none rounded-lg border border-red-400/30 bg-red-500/[0.06] p-2.5">
                  <p className="text-[11px] text-red-200 inline-flex items-start gap-1.5">
                    <AlertTriangle className="size-3 mt-0.5 flex-none" />
                    <span>{renderError}</span>
                  </p>
                  <div className="mt-2 flex items-center gap-3">
                    <button onClick={handleGenerate} className="inline-flex items-center gap-1 text-[11px] text-amber-200 hover:text-amber-100"><RefreshCw className="size-3" /> Retry</button>
                    <button onClick={() => setRenderError(null)} className="inline-flex items-center gap-1 text-[11px] text-zinc-300 hover:text-white">Continue with local preview</button>
                  </div>
                </div>
              )}
            </aside>

            {/* Footer notes under canvas */}
            <div className="lg:col-span-1 space-y-3">
              {isSignedIn === false && (
                <div className="rounded-2xl border border-amber-300/25 bg-amber-300/[0.05] p-4 flex items-center gap-3">
                  <Info className="size-4 text-amber-200 flex-none" />
                  <p className="text-[11px] text-zinc-300 leading-relaxed flex-1">
                    Saved on this device only. <Link to="/auth" search={{ redirect: "/studio" }} className="text-amber-200 underline-offset-2 hover:underline">Sign in</Link> to access it anywhere.
                  </p>
                </div>
              )}
              <p className="text-[10px] text-zinc-400 leading-relaxed">
                Browser previews preserve source lighting and texture for fast iteration. Real paint color may vary based on lighting, surface, and finish. For an accurate match, request a free in-person consultation.
              </p>
            </div>
          </div>
        ))}

        {activeTab === "explore" && <ColorExplorer />}

        {activeTab === "inspiration" && (
          <div className="space-y-10">
            <div>
              <h2 className="font-serif text-2xl sm:text-3xl text-white mb-3">Color Inspiration</h2>
              <p className="text-zinc-400 text-sm sm:text-base max-w-xl">
                Browse real transformations and curated palettes from Wisconsin homes. Every project starts with the right color story.
              </p>
            </div>
            <StudioDemoGallery />
          </div>
        )}
      </div>
      {/* Mobile sticky action bar */}
      {sourceUrl && activeTab === "paint" && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#050505]/95 backdrop-blur-xl safe-bottom">
          <div className="flex items-center gap-2 px-3 py-2.5">
            <button
              onClick={handleGenerate}
              disabled={isRendering || assignedCount === 0}
              className="flex-1 bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-40 min-h-[48px]"
            >
              {isRendering ? (<><Loader2 className="size-3.5 animate-spin" /> Rendering…</>) : (<><Wand2 className="size-3.5" /> Photorealistic{assignedCount > 0 ? ` (${assignedCount})` : ""}</>)}
            </button>
            <button
              onClick={goToEstimate}
              className="flex-1 bg-white text-zinc-950 font-semibold py-3 rounded-full text-xs min-h-[48px]"
            >Get Estimate</button>
          </div>
        </div>
      )}
      <ColorPickerDialog
        open={pickerExpanded}
        onOpenChange={setPickerExpanded}
        onSelect={onPickColor}
        surfaceLabel={
          selected.length === 0
            ? "any surface"
            : selected.length === 1
              ? (selectedSegments[0]?.label || SURFACE_LABELS[selectedSegments[0]?.kind ?? "walls"])
              : `${selected.length} selected surfaces`
        }
      />
    </section>
  );
}

// ---------- helpers ----------

function downloadCanvas(filename: string) {
  const c = document.querySelector("canvas");
  if (!c) return;
  const url = (c as HTMLCanvasElement).toDataURL("image/png");
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function ToolbarBtn({ onClick, disabled, icon, label }: { onClick: () => void; disabled?: boolean; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/5 text-[11px] text-zinc-300 hover:bg-white/10 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
    >
      {icon} {label}
    </button>
  );
}

function StudioEmptyFooter() {
  return (
    <div className="mt-10 sm:mt-14 flex flex-col md:flex-row items-center justify-between gap-8 border-t border-white/5 pt-10 max-w-2xl w-full mx-auto px-4">
      <div className="flex items-center gap-4">
        <div className="flex -space-x-2">
          <div className="w-8 h-8 rounded-full bg-neutral-800 border-2 border-[#030303] flex items-center justify-center text-[10px] text-amber-200 font-medium">SW</div>
          <div className="w-8 h-8 rounded-full bg-neutral-700 border-2 border-[#030303] flex items-center justify-center text-[10px] text-white font-medium">+</div>
          <div className="w-8 h-8 rounded-full bg-amber-600 border-2 border-[#030303] flex items-center justify-center text-[10px] text-white font-medium">AI</div>
        </div>
        <p className="text-xs text-zinc-500 max-w-[200px] leading-relaxed tracking-wide">
          Official Sherwin-Williams color matching engine integrated.
        </p>
      </div>
      <div className="flex gap-10 sm:gap-12">
        <div className="text-center">
          <div className="text-xl font-light text-white mb-0.5">1,726</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400">Color Options</div>
        </div>
        <div className="text-center">
          <div className="text-xl font-light text-white mb-0.5">&lt;100ms</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-400">Render Speed</div>
        </div>
      </div>
    </div>
  );
}

function StepCard({ icon, title, desc, n }: { icon: React.ReactNode; title: string; desc: string; n: number }) {
  return (
    <div className="relative rounded-2xl border border-white/5 bg-white/[0.02] p-5 text-center overflow-hidden group hover:border-amber-300/20 transition-colors duration-500">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-300/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="w-10 h-10 rounded-full bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300 mx-auto mb-3 transition-transform duration-500 group-hover:scale-110">
        {icon}
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-400 mb-1">Step {n}</div>
      <h3 className="text-sm font-semibold text-white mb-1.5">{title}</h3>
      <p className="text-xs text-zinc-500 leading-relaxed">{desc}</p>
    </div>
  );
}

function StudioHowItWorks() {
  return (
    <div className="mt-16 sm:mt-20 max-w-3xl w-full px-4">
      <p className="text-center text-[10px] uppercase tracking-widest text-zinc-400 mb-6">How it works</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StepCard n={1} icon={<ImageIcon className="size-5" />} title="Upload" desc="Snap a photo of your room or exterior." />
        <StepCard n={2} icon={<Wand2 className="size-5" />} title="Detect" desc="AI finds walls, trim, ceilings & cabinets." />
        <StepCard n={3} icon={<Sparkles className="size-5" />} title="Paint" desc="Try 1,726 Sherwin-Williams colors instantly." />
      </div>
    </div>
  );
}

function StudioDemoGallery() {
  const items = [
    { src: insp1.url, title: "Heritage Navy Living Room", palette: ["#0f1b3d", "#f5f0e0", "#c9a84c"], note: "Deep navy walls with crisp white trim — Madison craftsman." },
    { src: insp2.url, title: "Sage & Brass Kitchen", palette: ["#a8b8a0", "#f5f0e6", "#c9a84c"], note: "Soft sage cabinetry paired with warm brass fixtures." },
    { src: insp3.url, title: "Charcoal Exterior", palette: ["#2a2d34", "#f5f3ee", "#1a1a1a"], note: "Modern charcoal siding with bright white trim." },
    { src: insp4.url, title: "Warm Terracotta Retreat", palette: ["#c4654a", "#f5e9d8", "#e8d5b8"], note: "Cozy accent wall in warm terracotta and linen." },
    { src: insp5.url, title: "Classic Cream & Walnut", palette: ["#f5efdf", "#5c3a24", "#c9a06a"], note: "Timeless dining room with rich walnut wainscoting." },
    { src: insp6.url, title: "Forest Green Lake House", palette: ["#1a3c2a", "#f5f0e0", "#a8382c"], note: "Heritage forest green exterior with cream trim." },
  ];
  return (
    <div className="mt-12 sm:mt-16 w-full max-w-6xl mx-auto px-4 pb-16">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {items.map((it) => (
          <figure key={it.title} className="rounded-2xl border border-white/5 overflow-hidden bg-white/[0.02] hover:border-amber-300/20 transition-colors group">
            <div className="relative overflow-hidden">
              <img
                src={it.src}
                alt={it.title}
                loading="lazy"
                width={1200}
                height={900}
                className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-[1.03]"
              />
            </div>
            <figcaption className="p-4">
              <div className="flex items-center gap-1.5 mb-2">
                {it.palette.map((c) => (
                  <span key={c} className="w-4 h-4 rounded-full ring-1 ring-white/10" style={{ backgroundColor: c }} />
                ))}
              </div>
              <p className="font-serif text-base text-white">{it.title}</p>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{it.note}</p>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

function UploadCard({ onPick, isUploading, error }: { onPick: (f: File) => void; isUploading: boolean; error?: string | null }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const isMobile = useIsMobile();
  return (
    <div className="group relative max-w-2xl w-full mx-auto">
      <div className="absolute -inset-1 bg-gradient-to-r from-amber-900/20 via-neutral-800/20 to-amber-900/20 rounded-3xl blur opacity-25 group-hover:opacity-40 transition duration-1000" />
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault(); setDrag(false);
          const f = e.dataTransfer.files?.[0]; if (f) onPick(f);
        }}
        className={`relative rounded-3xl border overflow-hidden transition ${drag ? "border-amber-300/40 bg-amber-300/[0.04]" : "border-white/5 bg-white/[0.02]"}`}
      >
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)", backgroundSize: "24px 24px" }} />
        <div className="relative flex flex-col items-center justify-center text-center px-8 py-14 sm:py-20">
          <div className="mb-8 relative">
            <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full scale-0 group-hover:scale-150 transition-transform duration-700" />
            <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-neutral-800 to-[#181818] border border-white/10 flex items-center justify-center shadow-2xl transition-transform duration-500 group-hover:-translate-y-1">
              {isUploading ? <Loader2 className="size-8 sm:size-10 animate-spin text-amber-300" /> : <Upload className="size-8 sm:size-10 text-amber-300" />}
            </div>
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-white mb-3">Upload your space</h2>
          <p className="text-zinc-500 max-w-sm mb-8 sm:mb-10 text-sm tracking-wide">
            Drag and drop a photo of your room or exterior. For the best results, use a well-lit, head-on shot.
          </p>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              // Reset so re-selecting the same file (e.g. after an error) fires onChange again.
              e.target.value = "";
              if (f) onPick(f);
            }}
          />
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = "";
              if (f) onPick(f);
            }}
          />
          <div className="flex flex-col sm:flex-row gap-3 items-center">
            {isMobile && (
              <button
                onClick={() => cameraRef.current?.click()}
                disabled={isUploading}
                className="px-8 py-4 bg-amber-400 text-zinc-950 font-semibold rounded-full hover:bg-amber-300 transition-all duration-300 shadow-[0_0_40px_rgba(251,191,36,0.25)] active:scale-95 cursor-pointer inline-flex items-center gap-3 disabled:opacity-50 min-h-[48px]"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h2l2-2h6l2 2h2a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Take photo
              </button>
            )}
            <button
              onClick={() => inputRef.current?.click()}
              disabled={isUploading}
              className="px-10 py-4 bg-white text-zinc-950 font-semibold rounded-full hover:bg-neutral-200 transition-all duration-300 shadow-[0_0_40px_rgba(255,255,255,0.1)] hover:scale-105 active:scale-95 cursor-pointer inline-flex items-center gap-3 disabled:opacity-50 min-h-[48px]"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              {isUploading ? "Uploading…" : isMobile ? "Choose from library" : "Choose a photo"}
            </button>
          </div>
          {error && (
            <p className="mt-4 text-sm text-red-400 max-w-sm">{error}</p>
          )}
          <div className="mt-8 flex gap-4 items-center">
            <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Compatible with</span>
            <div className="flex gap-2 opacity-40">
              <span className="text-[10px] border border-zinc-700 px-2 py-0.5 rounded">JPG</span>
              <span className="text-[10px] border border-zinc-700 px-2 py-0.5 rounded">PNG</span>
              <span className="text-[10px] border border-zinc-700 px-2 py-0.5 rounded">HEIC</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}